import { motion } from "framer-motion";
import { Zap, Sun, Handshake, BatteryCharging, Cpu, ArrowUpRight } from "lucide-react";
import { MEDIA } from "@/lib/content";

const services = [
  {
    key: "ev",
    icon: Zap,
    title: "EV Charging Stations",
    overline: "Mobility infrastructure",
    description:
      "End-to-end DC fast-charging and AC destination-charging networks for fleets, malls, highways and residential complexes — hardware, software and 24×7 uptime ops.",
    bullets: ["CCS2 · CHAdeMO · Type-2", "OCPP 1.6J compliant", "Revenue share models"],
    image: MEDIA.ev,
    span: "md:col-span-7",
    ctaHref: "#contact",
    ctaLabel: "Discuss this service",
  },
  {
    key: "solar",
    icon: Sun,
    title: "Solar Projects",
    overline: "Rooftop & ground-mount",
    description:
      "From 50 kW rooftops to 20 MW open-access parks — EPC, O&M and retrofits engineered for Indian grid conditions.",
    bullets: ["Tier-1 modules", "25-year performance warranty"],
    image: MEDIA.solar,
    span: "md:col-span-5",
    ctaHref: "#contact",
    ctaLabel: "Discuss this service",
  },
  {
    key: "hybrid",
    icon: BatteryCharging,
    title: "Hybrid Inverters",
    overline: "Solar + storage + grid",
    description:
      "Single-unit hybrid inverters that intelligently blend solar, battery storage and grid power — so you get uninterrupted, lowest-cost electricity around the clock.",
    bullets: [
      "On-grid + off-grid ready",
      "Battery-first or grid-first modes",
      "Remote monitoring app",
      "3 kW – 100 kW range",
    ],
    image: MEDIA.hybrid,
    span: "md:col-span-6",
    ctaHref: "#contact",
    ctaLabel: "Discuss this service",
  },
  {
    key: "ppa",
    icon: Handshake,
    title: "Power Purchase Agreements",
    overline: "Zero-capex energy",
    description:
      "Switch to renewable power at a fixed, lower tariff for 15–25 years. We own, operate and maintain — you just buy the electricity.",
    bullets: ["0 upfront cost", "Up to 30% savings on tariff", "Full O&M coverage"],
    image: MEDIA.ppa,
    span: "md:col-span-6",
    ctaHref: "#contact",
    ctaLabel: "Discuss this service",
  },
  {
    key: "it",
    icon: Cpu,
    title: "IT Products",
    overline: "Software · platforms · automation",
    description:
      "We also build modern SaaS products that complement our energy services. Our flagship platform, Scanvio, is live now — explore it to see how we apply our engineering DNA beyond hardware.",
    bullets: ["SaaS platforms", "Workflow automation", "Mobile-first apps"],
    image: MEDIA.it,
    span: "md:col-span-12",
    ctaHref: "https://scanvio.app",
    ctaLabel: "Visit Scanvio.app",
    external: true,
  },
];

function ServiceCard({ s, i }) {
  const Icon = s.icon;
  const ctaProps = s.external
    ? { href: s.ctaHref, target: "_blank", rel: "noopener noreferrer" }
    : { href: s.ctaHref };
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      className={`group relative overflow-hidden rounded-lg border border-border bg-surface hover:shadow-lg transition-all duration-300 ${s.span}`}
      data-testid={`service-card-${s.key}`}
    >
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={s.image}
          alt={s.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/10 to-transparent" />
        <div className="absolute top-4 left-4 h-10 w-10 rounded-full bg-surface flex items-center justify-center">
          <Icon size={18} strokeWidth={1.75} className="text-primary" />
        </div>
      </div>
      <div className="p-6 md:p-8">
        <p className="overline mb-3">{s.overline}</p>
        <h3 className="font-heading text-2xl md:text-3xl font-medium text-primary tracking-tight">
          {s.title}
        </h3>
        <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
          {s.description}
        </p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {s.bullets.map((b) => (
            <li
              key={b}
              className="text-xs font-medium px-3 py-1 rounded-full bg-secondary text-secondary-foreground"
            >
              {b}
            </li>
          ))}
        </ul>
        <a
          {...ctaProps}
          data-testid={`service-cta-${s.key}`}
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:gap-2.5 transition-all"
        >
          {s.ctaLabel}
          <ArrowUpRight size={16} strokeWidth={2} />
        </a>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="section-pad relative"
    >
      <div className="container-outer">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <p className="overline mb-4">What we do</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-medium text-primary heading-tight">
              Five pillars. One mission.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Whether you operate a factory, a fleet or a fund — we compose the
              right mix of on-site solar, hybrid inverters, EV infrastructure,
              PPA finance and modern software to cut your energy bill, your
              carbon and your operational friction.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 auto-rows-min">
          {services.map((s, i) => (
            <ServiceCard s={s} i={i} key={s.key} />
          ))}
        </div>
      </div>
    </section>
  );
}
